import React, { useEffect, useState } from 'react'
import { makeStyles } from '@mui/styles'
import { Avatar, Box, Stack, Theme, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { NetworkType, UserAboSettings } from '@professocialApi/professocial-core-frontend-api'
import NoDataComponent from '@professocial/professocialFormComponents/FormComponents/NoDataComponent'
import PreloaderButton from '@professocial/professocialFormComponents/FormComponents/PreloaderButton'
import { PageAboSettings, UserHasPage } from '@professocialApi/professocial-core-frontend-api/models'
import { useSelector } from 'react-redux'
import { RootState } from '../../../store/accountRoot.reducer'
import { namedRequestData } from '../../../store/common/common.selectors'
import { forceDisconnectInitiated } from '../../../store/accounts/accounts.actions'

interface Props {
  settings: UserAboSettings;
  userHasPages?: UserHasPage[];
  disconnectPage: (userHasPage: UserHasPage) => void
}

const useStyles = makeStyles((theme: Theme) => ({
  formDivide: {
    Width: '100%',
    borderBottom: '1px solid #808080',
    lineHeight: '0.1em',
    margin: '10px 0 20px',
    textAlign: 'justify',
  },
  formDivide_text: {
    paddingRight: '10px',
  },
  formDivide_box: {
    padding: theme.spacing(0),
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    textAlign: 'center',
  },
  fbPage: {
    padding: theme.spacing(2),
    width: '100%',
  },
  fbPagePart: {
    width: '100%',
    paddingTop: 0,
  },
  fbPagePartColumn: {
    display: 'flex',
    flexDirection: 'column',
    flexBasis: '100%',
    flex: 1
  },
  fbPageInfo: {
    flexBasis: '15%',
  },
  fbPageDisconnect: {
    padding: theme.spacing(2),
  },
  fbAvatar: {
    width: theme.spacing(7),
    height: theme.spacing(7),
  },
  fbText: {
    paddingLeft: theme.spacing(1),
  },
}))

const getTitle = (type: NetworkType) => {
  if (type === NetworkType.Instagram) {
    return 'instaThemeChannels'
  }
  if (type === NetworkType.Facebook) {
    return 'fbThemeChannels'
  }
  if (type === NetworkType.GoogleMyBusiness) {
    return 'googleThemeChannels'
  }
}

const FacebookFanPage: React.FC<Props> = (props) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const [currentSettings, setCurrentSettings] = useState<PageAboSettings>()
  const [currentUserHasPage, setCurrentUserHasPage] = useState<UserHasPage>()
  const [pageLink, setPageLink] = useState<string | undefined>()

  const onForceDisconnect = useSelector((state: RootState) => namedRequestData(state, forceDisconnectInitiated.type))

  useEffect(() => {
    props.settings.topics.map(abo => {
      const setting = props.settings.aboSettings.find(setting => abo.id === setting.topicId)

      if (setting) {
        setting.pageIdentifier.networkType === NetworkType.Facebook
          && setPageLink('https://www.facebook.com/' + setting.pageIdentifier.networkPageId)
        setting.pageIdentifier.networkType === NetworkType.Instagram
          && setPageLink('https://www.instagram.com/' + setting.networkPageName)

        setCurrentSettings(setting)
        setCurrentUserHasPage(props.userHasPages?.find(value => value.networkPageId === setting.pageIdentifier.networkPageId))
      }
    })
  }, [props.settings])

  return (
    <Stack direction='column' sx={{ p: 2 }} spacing={3}>
      <Box className={classes.fbPageInfo}>
        <Stack direction="row" alignItems='center' spacing={2}>
          <Box>
            <Avatar className={classes.fbAvatar} src={currentSettings?.networkProfileImageUrl}/>
          </Box>
          <Box>
            <Typography className={classes.fbText}>
              <a rel="noreferrer" target="_blank" href={pageLink}>{currentSettings?.networkPageName}</a>
            </Typography>
            <Typography className={classes.fbText}>
              <b>{currentSettings?.networkPageFanCount}&nbsp;</b>{t('accountsCommon:facebookFans')}
            </Typography>
          </Box>
          <Box className={classes.fbPageDisconnect}>
            <PreloaderButton
              variant="contained"
              color="primary"
              position='left'
              text={t('accountsCommon:disconnect')}
              loading={onForceDisconnect?.inProgress && onForceDisconnect?.id === currentUserHasPage?.networkPageId}
              disableButton={(onForceDisconnect?.status === 'pending' && onForceDisconnect?.inProgress && onForceDisconnect?.id === currentUserHasPage?.networkPageId)}
              onClick={() => {
                if (currentUserHasPage) {
                  const { networkType, networkPageId, networkUserId, isPageAdmin } = currentUserHasPage
                  const params: UserHasPage | undefined = {
                    networkType,
                    networkPageId,
                    networkUserId,
                    isPageAdmin
                  } as UserHasPage

                  params && props.disconnectPage(params)
                }
              }}
            />
          </Box>
        </Stack>
      </Box>
      {props.settings && (
        <Box className={classes.fbPagePart} sx={{ pl: 0, pr: 4, pb: 4 }}>
          {currentSettings?.pageIdentifier && <Typography sx={{ fontWeight: 'bold', mb: 1 }}>
            {t(`accountsCommon:${getTitle(currentSettings.pageIdentifier.networkType)}`)}
          </Typography>}

          {!!props.settings.topics && !!props.settings.topics.length
            ? props.settings.topics.map((abo, index) => {
              const setting = props.settings.aboSettings.find(setting => abo.id === setting.topicId)

              return (
                <Stack key={index} direction='row' sx={{ width: '100%' }}>
                  <Box className={classes.fbPagePartColumn}>
                    <Typography>{abo.name}</Typography>
                  </Box>
                  <Box className={classes.fbPagePartColumn}>
                    <Typography> - {setting ? setting.aboType.toUpperCase() : t('accountsCommon:aboOff')}</Typography>
                  </Box>
                </Stack>
              )
            })
            : <NoDataComponent noDataText={t('accountsCommon:noAboTopics')} alignment="left"/>}
        </Box>
      )}
    </Stack>
  )
}

export default FacebookFanPage
